import { cn } from '@quadralize/utils'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { motion } from 'framer-motion'
import { X } from 'lucide-react'
import * as React from 'react'
import { useMobile } from '../../../hooks/use-mobile'

const MAX_HEIGHT_DESKTOP = '85vh'

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from '../drawer/drawer'

const DialogResponsiveContext = React.createContext<{ isMobile: boolean }>({ isMobile: false })

const Dialog = ({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) => {
  const isMobile = useMobile()

  return (
    <DialogResponsiveContext.Provider value={{ isMobile }}>
      {isMobile ? <Drawer {...props} /> : <DialogPrimitive.Root {...props} />}
    </DialogResponsiveContext.Provider>
  )
}

const DialogTrigger = ({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) => {
  const { isMobile } = React.useContext(DialogResponsiveContext)

  if (isMobile) {
    return <DrawerTrigger {...props} />
  }

  return <DialogPrimitive.Trigger {...props} />
}

const DialogPortal = ({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) => {
  const { isMobile } = React.useContext(DialogResponsiveContext)

  if (isMobile) {
    return <DrawerPortal {...props} />
  }

  return <DialogPrimitive.Portal {...props} />
}

const DialogClose = ({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) => {
  const { isMobile } = React.useContext(DialogResponsiveContext)

  if (isMobile) {
    return <DrawerClose {...props} />
  }

  return <DialogPrimitive.Close {...props} />
}

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    asChild
    className={cn('fixed inset-0 z-50 bg-black/40 backdrop-blur-sm', className)}
    {...props}
  >
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0"
    />
  </DialogPrimitive.Overlay>
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showClose?: boolean
    closeOnOverlayClick?: boolean
  }
>(({ className, children, showClose = true, closeOnOverlayClick = true, ...props }, ref) => {
  const { isMobile } = React.useContext(DialogResponsiveContext)

  if (isMobile) {
    return (
      <DrawerContent ref={ref} className={className} {...props}>
        {children}
      </DrawerContent>
    )
  }

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        onPointerDownOutside={(event: Event) => {
          if (!closeOnOverlayClick) {
            event.preventDefault()
          }
        }}
        onInteractOutside={(event: Event) => {
          if (!closeOnOverlayClick) {
            event.preventDefault()
          }
        }}
        style={{ maxHeight: MAX_HEIGHT_DESKTOP }}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 flex flex-col w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-hidden border border-border-strong bg-bg-elevated shadow-md rounded-xl outline-none',
          'data-[state=open]:animate-content-show data-[state=closed]:animate-content-hide',
          className,
        )}
        {...props}
      >
        {children}
        {showClose && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-bg-base transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-bg-muted data-[state=open]:text-text-tertiary z-50">
            <X className="h-4 w-4" strokeWidth={2.5} />
            <span className="sr-only">Fechar</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { isMobile } = React.useContext(DialogResponsiveContext)

  if (isMobile) {
    return <DrawerHeader className={className} {...props} />
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 text-center sm:text-left flex-none p-6 pb-2 outline-none',
        className,
      )}
      {...props}
    />
  )
}
DialogHeader.displayName = 'DialogHeader'

const DialogBody = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn('flex-1 w-full min-h-0 overflow-y-auto overflow-x-hidden', className)}
      {...props}
    >
      <div className="px-4 py-2 sm:px-6">{children}</div>
    </div>
  )
}
DialogBody.displayName = 'DialogBody'

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { isMobile } = React.useContext(DialogResponsiveContext)

  if (isMobile) {
    return <DrawerFooter className={className} {...props} />
  }

  return (
    <div
      className={cn(
        'flex flex-col-reverse sm:flex-row sm:justify-end gap-2 flex-none p-6 pt-2 outline-none',
        className,
      )}
      {...props}
    />
  )
}
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  const { isMobile } = React.useContext(DialogResponsiveContext)

  if (isMobile) {
    return <DrawerTitle ref={ref} className={className} {...props} />
  }

  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        'text-lg font-semibold leading-none tracking-tight text-text-primary',
        className,
      )}
      {...props}
    />
  )
})
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  const { isMobile } = React.useContext(DialogResponsiveContext)

  if (isMobile) {
    return <DrawerDescription ref={ref} className={className} {...props} />
  }

  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-text-secondary', className)}
      {...props}
    />
  )
})
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
